import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Sector } from '@model/LuckyDrawDto'

import AnimateFrameEN from './assets/anim.svg'
import TopArrow from './assets/wheel-arrow.svg'
import WheelFrameEN from './assets/wheel-frame.svg'

import styles from './LuckyDraw.module.scss'

interface LuckyDrawProps {
  winnerIndex: number
  sectors: Sector[]
  setWon: () => void
}

export interface WheelRef {
  startSpinning(): void
}

interface SpinData {
  tot: number
  dia: number
  rad: number
  pi: number
  tau: number
  arc: number
  friction: number
  angVelMin: number
  angVelMax: number
  angVel: number
  ang: number
  rounds: number
}

export const LuckyDraw = forwardRef<WheelRef, LuckyDrawProps>(
  ({ winnerIndex, sectors, setWon }, ref) => {
    // Initial visual rotation requirement baseline: 37.093deg (index 0 visually on the right)
    // For mobile widths index 0 visually at the bottom: baseline + 90deg = 127.093deg.
    const MOBILE_BREAKPOINT = 768
    const BASE_ROTATION_DESKTOP_DEG = 37.093
    const BASE_ROTATION_MOBILE_DEG = 127.093

    const locale = useSessionLanguage()

    // For Arabic on desktop we want index 0 visually on the LEFT (opposite side) => +180deg
    const desktopRotationForLocale = (loc: string) =>
      loc === 'ar' ? BASE_ROTATION_DESKTOP_DEG + 180 : BASE_ROTATION_DESKTOP_DEG

    const normalizeDeg = (deg: number) => ((deg % 360) + 360) % 360

    const computeInitialRotationDeg = (width: number) => {
      if (width <= MOBILE_BREAKPOINT) {
        return BASE_ROTATION_MOBILE_DEG
      }
      return normalizeDeg(desktopRotationForLocale(locale))
    }

    const initialRotationRadRef = useRef<number>(
      (() => {
        const initialDeg = computeInitialRotationDeg(window.innerWidth)
        return (initialDeg * Math.PI) / 180
      })()
    )

    const getPhaseRad = () => initialRotationRadRef.current + Math.PI / 2

    const containerRef = useRef<HTMLDivElement>(null)
    const animImgRef = useRef<HTMLImageElement>(null)
    const spinData = useRef<SpinData>({
      tot: sectors.length,
      dia: 0,
      rad: 0,
      pi: Math.PI,
      tau: 2 * Math.PI,
      arc: (2 * Math.PI) / sectors.length,
      friction: 0.995,
      angVelMin: 0.002,
      angVelMax: 0,
      angVel: 0,
      ang: initialRotationRadRef.current + Math.PI / 2,
      rounds: 8,
    })
    const started = useRef<boolean>(false)

    const isAccelerating = useRef(false)
    const isSpinning = useRef(false)
    const onWinner = useRef(false)
    const animationFrameId = useRef<number | null>(null)

    const getIndex = () => {
      const { tot, ang, tau, arc } = spinData.current
      if (tot && ang && tau && arc) {
        const phaseRad = getPhaseRad()
        const norm = (ang - phaseRad + tau) % tau
        const fromPointer = (tau - norm) % tau
        const idx = Math.floor((fromPointer + arc / 2) / arc) % tot
        return idx
      }
      return 0
    }

    const rotate = () => {
      if (!spinData.current) return
      const { ang, pi } = spinData.current

      if (animImgRef.current) {
        animImgRef.current.style.transform = `rotate(${ang - pi / 2}rad)`
        return
      }
    }

    const frame = useCallback(() => {
      if (!spinData.current || !isSpinning.current) {
        return
      }

      const data = spinData.current

      if (data.angVel >= data.angVelMax) {
        isAccelerating.current = false
      }

      if (getIndex() !== winnerIndex) {
        onWinner.current = false
      }
      if (getIndex() === winnerIndex) {
        if (!onWinner.current) {
          data.rounds--
        }
        onWinner.current = true
        if (data.rounds < 1) {
          isAccelerating.current = false
          data.angVel *= 0.94
        }
      }

      // Accelerate
      if (isAccelerating.current) {
        data.angVel ||= data.angVelMin
        data.angVel *= 1.06
      } else {
        data.angVel *= data.friction

        // SPIN END:
        if (data.angVel < data.angVelMin) {
          if (started.current) {
            setWon()
          }
          isSpinning.current = false
          data.angVel = 0
          data.rounds = 8
        }
      }

      data.ang += data.angVel // Update angle
      data.ang %= data.tau // Normalize angle

      rotate()
    }, [setWon, winnerIndex, getIndex])

    useImperativeHandle(ref, () => ({
      startSpinning() {
        if (isSpinning.current) {
          return
        }
        started.current = true
        isSpinning.current = true
        isAccelerating.current = true
        spinData.current.angVelMax = 0.25
      },
    }))

    useEffect(() => {
      spinData.current.tot = sectors.length
      spinData.current.arc = (2 * Math.PI) / sectors.length

      if (containerRef.current && containerRef.current.clientWidth) {
        const size = containerRef.current.clientWidth
        spinData.current.dia = size
        spinData.current.rad = size / 2
      }

      rotate()

      const engine = () => {
        frame()
        animationFrameId.current = requestAnimationFrame(engine)
      }

      if (!animationFrameId.current) {
        engine()
      }

      return () => {
        if (animationFrameId.current) {
          cancelAnimationFrame(animationFrameId.current)
          animationFrameId.current = null
        }
      }
    }, [frame, sectors])

    useEffect(() => {
      const onResize = () => {
        const w = window.innerWidth
        const desiredDeg = computeInitialRotationDeg(w)
        const desiredRad = (desiredDeg * Math.PI) / 180
        if (!isSpinning.current && Math.abs(desiredRad - initialRotationRadRef.current) > 1e-6) {
          initialRotationRadRef.current = desiredRad
          spinData.current.ang = desiredRad + Math.PI / 2
          rotate()
        }
      }

      if (typeof window !== 'undefined') {
        window.addEventListener('resize', onResize)

        onResize()
      }
      return () => {
        if (typeof window !== 'undefined') {
          window.removeEventListener('resize', onResize)
        }
      }
    }, [])

    return (
      <div ref={containerRef} className={styles.wheelOfFortune} id='wheelOfFortune'>
        <img src={WheelFrameEN} alt='Wheel frame' className={styles.wheelBg} />

        <div className={styles.wheelWrapper}>
          <img
            ref={animImgRef}
            src={AnimateFrameEN}
            alt='Wheel animation layer'
            className={styles.wheelAnim}
          />{' '}
          <img src={TopArrow} alt='Wheel top arrow' className={styles.wheelTop} />
        </div>
      </div>
    )
  }
)
